import React, { useMemo } from 'react';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
};

const getMetric = (point) => {
  if (Number.isFinite(Number(point?.attenuation_db))) return Number(point.attenuation_db);
  if (Number.isFinite(Number(point?.rx_power_dbm))) return Math.abs(Number(point.rx_power_dbm));
  return null;
};

const getConnectionColor = (status) => {
  if (status === 'online') return '#22c55e';
  if (status === 'offline') return '#ef4444';
  return '#9ca3af';
};

const SignalHistoryChart = ({ history = [] }) => {
  const chart = useMemo(() => {
    const normalized = (Array.isArray(history) ? history : [])
      .map((item) => ({
        ...item,
        metric: getMetric(item),
      }))
      .filter((item) => item.metric !== null);

    if (normalized.length === 0) {
      return {
        points: [],
        labels: [],
        min: 0,
        max: 0,
        path: '',
      };
    }

    const minMetric = Math.min(...normalized.map((item) => item.metric));
    const maxMetric = Math.max(...normalized.map((item) => item.metric));
    const min = Math.floor(Math.min(0, minMetric - 1));
    const max = Math.ceil(maxMetric + 1);
    const width = 100;
    const height = 100;

    const points = normalized.map((item, index) => {
      const x = normalized.length === 1 ? 50 : (index / (normalized.length - 1)) * width;
      const ratio = max === min ? 0.5 : (item.metric - min) / (max - min);
      const y = height - (ratio * height);
      return {
        ...item,
        x: clamp(x, 0, width),
        y: clamp(y, 0, height),
      };
    });

    const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
    const labels = [points[0], points[Math.floor(points.length / 2)], points[points.length - 1]].filter(Boolean);

    return { points, labels, min, max, path };
  }, [history]);

  if (chart.points.length === 0) {
    return <p className="text-gray-500 text-sm">Belum ada histori redaman yang tersimpan.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-3">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-40 w-full overflow-visible">
          {[0, 25, 50, 75, 100].map((value) => (
            <line key={value} x1="0" y1={value} x2="100" y2={value} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
          ))}
          <path d={chart.path} fill="none" stroke="#38bdf8" strokeWidth="2.2" strokeLinecap="round" />
          {chart.points.map((point) => (
            <circle key={point.id || point.captured_at} cx={point.x} cy={point.y} r="2.5" fill={getConnectionColor(point.connection_status)} />
          ))}
        </svg>
      </div>

      <div className="grid sm:grid-cols-3 gap-2 text-xs text-gray-400">
        {chart.labels.map((point, index) => (
          <div key={`${point.captured_at}-${index}`} className="rounded-lg border border-white/10 bg-[#0A0A0A] p-2">
            <p className="text-white font-semibold">{point.metric?.toFixed(2)} dB</p>
            <p>{formatTime(point.captured_at)}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>Min skala: {chart.min.toFixed(0)} dB</span>
        <span>Maks skala: {chart.max.toFixed(0)} dB</span>
      </div>
    </div>
  );
};

export default SignalHistoryChart;
